import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import PhoneInput from "./PhoneInput";
import { submitBooking } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useSettings } from "../lib/settings";
import { fireGoogleAdsConversion } from "../lib/googleAds";
import { findPrivateTier, privateTierPrice } from "../lib/utils";
import { useAutoTranslate } from "../lib/autoTranslate";
import { useToast } from "../hooks/use-toast";
import { Loader2, CheckCircle2 } from "lucide-react";

const BookingDialog = ({ open, onOpenChange, tour, forcePrivate }) => {
  const { t, lang } = useI18n();
  const { settings } = useSettings();
  const { toast } = useToast();
  const navigate = useNavigate();
  const translatedTourTitle = useAutoTranslate(tour?.title || "");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone_country_code: "+1",
    phone: "",
    tour_date: "",
    guests: 2,
    adults: 2,
    children: 0,
    arrival_flight_number: "",
    arrival_datetime: "",
    departure_flight_number: "",
    departure_datetime: "",
    message: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const currentGuestCount = tour?.child_price
    ? Number(form.adults || 0) + Number(form.children || 0)
    : Number(form.guests || 0);
  const matchedTier = forcePrivate ? findPrivateTier(tour?.private_pricing_tiers, currentGuestCount) : null;
  const tierPrice = matchedTier ? privateTierPrice(matchedTier, currentGuestCount) : null;
  const isContactTier = matchedTier?.mode === "contact";
  const privatePP = tierPrice?.mode === "per_person" ? tierPrice.pricePerPerson : null;
  const privateTotal = tierPrice ? tierPrice.total : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!tour) return;
    setSubmitting(true);
    try {
      await submitBooking({
        tour_id: tour.id,
        tour_title: tour.title,
        full_name: form.full_name,
        email: form.email,
        phone: form.phone || null,
        phone_country_code: form.phone ? form.phone_country_code : null,
        tour_date: form.tour_date,
        guests: tour.child_price ? Number(form.adults || 0) + Number(form.children || 0) : Number(form.guests),
        adults: tour.child_price ? Number(form.adults || 0) : null,
        children: tour.child_price ? Number(form.children || 0) : null,
        private: !!forcePrivate,
        private_price_per_person: privatePP,
        private_total_price: privateTotal,
        arrival_flight_number: tour.requires_flight_details ? (form.arrival_flight_number || null) : null,
        arrival_datetime: tour.requires_flight_details ? (form.arrival_datetime || null) : null,
        departure_flight_number: tour.requires_flight_details ? (form.departure_flight_number || null) : null,
        departure_datetime: tour.requires_flight_details ? (form.departure_datetime || null) : null,
        message: form.message || null,
        language: lang,
      });
      setSubmitted(true);
      fireGoogleAdsConversion(settings?.google_ads_booking_send_to);
      toast({
        title: "Booking request received",
        description: "Check your email for a confirmation. We'll follow up with a payment link.",
      });
      setTimeout(() => {
        onOpenChange(false);
        navigate("/thank-you", { state: { bookingTourTitle: tour.title } });
      }, 1400);
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: err?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setSubmitted(false);
      setForm({ full_name: "", email: "", phone_country_code: "+1", phone: "", tour_date: "", guests: 2, adults: 2, children: 0, arrival_flight_number: "", arrival_datetime: "", departure_flight_number: "", departure_datetime: "", message: "" });
    }, 200);
  };

  if (!tour) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-[560px] max-h-[90vh] overflow-y-auto bg-[#fffdf7]">
        {submitted ? (
          <div className="py-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="text-green-600" size={32} />
            </div>
            <h3 className="font-serif-display text-[26px] leading-tight">You're on the list.</h3>
            <p className="mt-3 text-[15px] text-[var(--ri-ink-soft)] max-w-[380px] mx-auto">
              We received your booking request for <strong>{translatedTourTitle}</strong>. Check your
              email within a day for a secure payment link and confirmation.
            </p>
            <button onClick={close} className="btn-primary mt-6">Close</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif-display text-[24px] leading-tight pr-6">
                {t("book_this_tour")}
              </DialogTitle>
              <DialogDescription className="text-[14px] text-[var(--ri-muted)]">
                {translatedTourTitle} · {forcePrivate ? `${tour.private_price || tour.price} (private)` : tour.price}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="bk-name" className="text-[13px] text-[var(--ri-ink-soft)]">{t("full_name")}</Label>
                  <Input id="bk-name" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="bk-email" className="text-[13px] text-[var(--ri-ink-soft)]">{t("email")}</Label>
                  <Input id="bk-email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1" />
                </div>
              </div>

              <PhoneInput
                id="bk-phone"
                label={`${t("phone")} · WhatsApp`}
                code={form.phone_country_code}
                number={form.phone}
                onCodeChange={(v) => update("phone_country_code", v)}
                onNumberChange={(v) => update("phone", v)}
              />

              {tour.child_price ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="bk-adults" className="text-[13px] text-[var(--ri-ink-soft)]">Adults (12+)</Label>
                    <Input
                      id="bk-adults"
                      type="number"
                      min={1}
                      max={30}
                      required
                      value={form.adults}
                      onChange={(e) => update("adults", e.target.value)}
                      className="mt-1 h-11 text-[15px]"
                    />
                    <p className="mt-1 text-[11px] text-[var(--ri-muted)]">{tour.price}</p>
                  </div>
                  <div>
                    <Label htmlFor="bk-children" className="text-[13px] text-[var(--ri-ink-soft)]">Children (6–11)</Label>
                    <Input
                      id="bk-children"
                      type="number"
                      min={0}
                      max={30}
                      value={form.children}
                      onChange={(e) => update("children", e.target.value)}
                      className="mt-1 h-11 text-[15px]"
                    />
                    <p className="mt-1 text-[11px] text-[var(--ri-muted)]">{tour.child_price}</p>
                  </div>
                </div>
              ) : (
                <div>
                  <Label htmlFor="bk-guests" className="text-[13px] text-[var(--ri-ink-soft)]">{t("guests")}</Label>
                  <Input id="bk-guests" type="number" min={1} max={30} required value={form.guests} onChange={(e) => update("guests", e.target.value)} className="mt-1 h-11 text-[15px]" />
                </div>
              )}

              {forcePrivate && isContactTier && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-[13.5px] text-amber-800">
                  For a group of {currentGuestCount}, we'll send you a custom quote after you submit this request.
                </div>
              )}

              {forcePrivate && tierPrice && !isContactTier && (
                <div className="rounded-lg bg-[var(--ri-cream)] px-3 py-2.5 text-[13.5px]">
                  {tierPrice.mode === "per_person" ? (
                    <span className="text-[var(--ri-ink-soft)]">€{tierPrice.pricePerPerson} per person for {currentGuestCount} guest{currentGuestCount === 1 ? "" : "s"} · </span>
                  ) : (
                    <span className="text-[var(--ri-ink-soft)]">Flat price for {currentGuestCount} guest{currentGuestCount === 1 ? "" : "s"} · </span>
                  )}
                  <span className="font-semibold">Total: €{tierPrice.total}</span>
                </div>
              )}

              <div>
                <Label htmlFor="bk-date" className="text-[13px] text-[var(--ri-ink-soft)]">{t("tour_date")}</Label>
                <Input id="bk-date" type="date" required value={form.tour_date} onChange={(e) => update("tour_date", e.target.value)} className="mt-1" />
              </div>

              {tour.requires_flight_details && (
                <div className="rounded-lg border border-[var(--ri-border)] p-3 space-y-3">
                  <p className="text-[12.5px] font-medium text-[var(--ri-ink-soft)]">Flight details (for your layover)</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="bk-arr-flight" className="text-[12.5px] text-[var(--ri-ink-soft)]">Arrival flight number</Label>
                      <Input id="bk-arr-flight" required value={form.arrival_flight_number} onChange={(e) => update("arrival_flight_number", e.target.value)} placeholder="e.g. TK1234" className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="bk-arr-time" className="text-[12.5px] text-[var(--ri-ink-soft)]">Arrival date & time</Label>
                      <Input id="bk-arr-time" type="datetime-local" required value={form.arrival_datetime} onChange={(e) => update("arrival_datetime", e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="bk-dep-flight" className="text-[12.5px] text-[var(--ri-ink-soft)]">Departure flight number</Label>
                      <Input id="bk-dep-flight" required value={form.departure_flight_number} onChange={(e) => update("departure_flight_number", e.target.value)} placeholder="e.g. TK5678" className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="bk-dep-time" className="text-[12.5px] text-[var(--ri-ink-soft)]">Departure date & time</Label>
                      <Input id="bk-dep-time" type="datetime-local" required value={form.departure_datetime} onChange={(e) => update("departure_datetime", e.target.value)} className="mt-1" />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="bk-msg" className="text-[13px] text-[var(--ri-ink-soft)]">{t("special_requests")}</Label>
                <Textarea id="bk-msg" rows={3} value={form.message} onChange={(e) => update("message", e.target.value)} className="mt-1" />
              </div>

              <button type="submit" disabled={submitting} className="btn-primary w-full inline-flex items-center justify-center gap-2">
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {t("submit_booking")}
              </button>
              <p className="text-[12px] text-[var(--ri-muted)] text-center">
                We'll email you a secure Stripe/PayPal payment link within a day.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingDialog;
